import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Plus, Power } from "lucide-react";
import { del, get, post, put } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { DataTable } from "@/components/common/DataTable";
import { LoadingState, ErrorState } from "@/components/common/States";
import { ConfirmModal } from "@/components/atelier/AtelierModals";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const inputCls = "h-9 w-full bg-input border border-border rounded-sm px-2 text-sm";

export const ActifBadge = ({ actif }) => (
  <span className={cn("font-mono text-[10px] uppercase px-1.5 py-0.5 border rounded-sm", actif === false ? "border-zinc-700 text-zinc-500" : "border-emerald-500/30 text-emerald-400 bg-emerald-500/10")}>{actif === false ? "inactif" : "actif"}</span>
);

export const FieldInput = ({ field, value, onChange, options }) => {
  const f = field;
  if (f.type === "bool") return <label className="flex items-center gap-2 h-9 text-sm"><input type="checkbox" data-testid={`form-${f.key}`} checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4" />{f.label}</label>;
  if (f.type === "select") return (
    <select data-testid={`form-${f.key}`} className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : (f.numeric ? Number(e.target.value) : e.target.value))} required={f.required}>
      {!f.required && <option value="">—</option>}
      {(options || f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>);
  if (f.type === "color") return <div className="flex gap-2"><input type="color" data-testid={`form-${f.key}`} value={value || "#e74c3c"} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 bg-input border border-border rounded-sm" /><input className={inputCls + " font-mono"} value={value || ""} onChange={(e) => onChange(e.target.value)} /></div>;
  return <input data-testid={`form-${f.key}`} type={f.type || "text"} step={f.type === "number" ? "any" : undefined} className={cn(inputCls, (f.type === "number" || f.mono) && "font-mono")} value={value ?? ""} placeholder={f.placeholder} required={f.required}
    onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} />;
};

export const CrudDialog = ({ open, onClose, title, fields, initial, onSubmit, busy, optionsFor, submitLabel }) => {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF(Object.fromEntries(fields.map((x) => [x.key, initial?.[x.key] ?? x.default ?? (x.type === "bool" ? false : "")]))); }, [open, initial, fields]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-auto" data-testid="crud-dialog">
        <DialogHeader><DialogTitle className="text-base">{title}</DialogTitle></DialogHeader>
        <form className="grid grid-cols-2 gap-3" onSubmit={(e) => { e.preventDefault(); onSubmit(f); }}>
          {fields.filter((x) => !x.hidden || !x.hidden(f, initial)).map((x) => (
            <div key={x.key} className={cn("space-y-1", x.span && "col-span-2")}>
              {x.type !== "bool" && <span className="eyebrow">{x.label}{x.required && " *"}</span>}
              <FieldInput field={x} value={f[x.key]} onChange={(v) => setF((p) => ({ ...p, [x.key]: v }))} options={optionsFor?.(x.key)} />
              {x.help && <div className="text-[10px] text-muted-foreground">{x.help}</div>}
            </div>
          ))}
          <div className="col-span-2 flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} data-testid="crud-cancel">Annuler</Button>
            <Button type="submit" disabled={busy} data-testid="crud-submit">{submitLabel || (initial ? "Enregistrer" : "Ajouter")}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const useCrud = (endpoint, queryKey) => {
  const qc = useQueryClient();
  const { entrepriseId } = useAuth();
  const list = useQuery({ queryKey: [queryKey, entrepriseId], queryFn: () => get(`/${endpoint}`) });
  const invalidate = () => qc.invalidateQueries({ queryKey: [queryKey] });
  const create = useMutation({ mutationFn: (b) => post(`/${endpoint}`, b), onSuccess: () => { toast.success("Ajouté"); invalidate(); }, onError: (e) => toast.error(e.message) });
  const update = useMutation({ mutationFn: ({ id, ...b }) => put(`/${endpoint}/${id}`, b), onSuccess: () => { toast.success("Modifié"); invalidate(); }, onError: (e) => toast.error(e.message) });
  const remove = useMutation({ mutationFn: (id) => del(`/${endpoint}/${id}`), onSuccess: () => { toast.success("Désactivé"); invalidate(); }, onError: (e) => toast.error(e.message) });
  return { list, create, update, remove, invalidate };
};

export const ReferentielPage = ({ title, subtitle, crumb, endpoint, queryKey, fields, columns, optionsFor, extraActions, canUpdate = true, toBody = (f) => f, rowExtra }) => {
  const { list, create, update, remove } = useCrud(endpoint, queryKey || endpoint);
  const [dialog, setDialog] = useState(null); // null | "new" | row
  const [toDisable, setToDisable] = useState(null);
  const rows = useMemo(() => [...(list.data || [])].sort((a, b) => (b.actif === false ? 0 : 1) - (a.actif === false ? 0 : 1) || a.id - b.id), [list.data]);
  const cols = [
    ...columns,
    { key: "actif", header: "État", render: (r) => <ActifBadge actif={r.actif} /> },
    { key: "_actions", header: "", align: "right", render: (r) => (
      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        {extraActions?.(r)}
        {canUpdate && <button data-testid={`edit-${r.id}`} title="Modifier" onClick={() => setDialog(r)} className="h-6 w-6 flex items-center justify-center rounded-sm hover:bg-accent text-muted-foreground hover:text-foreground"><Pencil className="h-3.5 w-3.5" /></button>}
        {r.actif !== false && <button data-testid={`disable-${r.id}`} title="Désactiver" onClick={() => setToDisable(r)} className="h-6 w-6 flex items-center justify-center rounded-sm hover:bg-red-950/50 text-muted-foreground hover:text-red-400"><Power className="h-3.5 w-3.5" /></button>}
      </div>) },
  ];
  const submit = async (f) => {
    const body = toBody(f, dialog === "new" ? null : dialog);
    if (dialog === "new") await create.mutateAsync(body).then(() => setDialog(null)).catch(() => {});
    else await update.mutateAsync({ id: dialog.id, ...body }).then(() => setDialog(null)).catch(() => {});
  };
  return (
    <div data-testid={`admin-${endpoint}-page`} className="space-y-3">
      <PageHeader crumbs={[{ label: "ProdTrack" }, { label: crumb || "Référentiels" }, { label: title }]} title={title} subtitle={subtitle}
        actions={<Button size="sm" className="h-8 font-semibold" onClick={() => setDialog("new")} data-testid="add-button"><Plus className="h-4 w-4 mr-1" />Ajouter</Button>} />
      <Panel title={`${title} (${rows.filter((r) => r.actif !== false).length} actif(s) / ${rows.length})`} testId="ref-panel">
        {list.isLoading ? <LoadingState /> : list.error ? <ErrorState error={list.error} onRetry={list.refetch} />
          : <DataTable testId="ref-table" columns={cols} rows={rows} rowKey={(r) => r.id} emptyTitle="Aucun élément" emptyHint="Utilisez « Ajouter » pour créer le premier enregistrement." />}
      </Panel>
      {rowExtra}
      <CrudDialog open={!!dialog} onClose={() => setDialog(null)} title={dialog === "new" ? `Ajouter — ${title}` : `Modifier — ${title}`} fields={fields} initial={dialog === "new" ? null : dialog} onSubmit={submit} busy={create.isPending || update.isPending} optionsFor={optionsFor} />
      {toDisable && <ConfirmModal open onClose={() => setToDisable(null)} title="Désactiver ?" tone="danger" confirmLabel="Désactiver" busy={remove.isPending}
        text={`« ${toDisable.nom || toDisable.code || toDisable.email} » sera désactivé (conservé dans l'historique, plus proposé dans les formulaires).`}
        onConfirm={() => remove.mutateAsync(toDisable.id).finally(() => setToDisable(null))} />}
    </div>
  );
};
